import { ReactNode, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ButtonBase, Popover } from '@mui/material';

import { useContactSupportPermissionGet } from '@sbv-clients/data-access-api';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import {
  Box,
  LetterAvatar,
  OverlayLoader,
  printErrorToConsole,
  useAccessToken,
  useConfirmationModal,
  useLoading,
  useTheme,
} from '@sbv-clients/ui-shared';

import { SupportFacilitySwitcher } from '../navbar/support-facility-switcher';
import { NavbarMenuItem } from '../navbar/typings/navbar-props';
import { useProfileSwitch } from '../navbar/use-profile-switch';
import { NavIcon, NavIconName } from './nav-icons';

export type AccountMenuOptions = {
  ssoUserName?: string;
  showPreferences?: boolean;
  showResetConfiguration?: boolean;
  showRemoteSupport?: boolean;
  showContactUs?: boolean;
  showProfiles?: boolean;
  extraMenuItems?: NavbarMenuItem[];
};

const MenuRow = ({
  icon,
  label,
  trailing,
  danger,
  disabled,
  onClick,
}: {
  icon: NavIconName | ReactNode;
  label: string;
  trailing?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) => {
  const theme = useTheme();
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: '100%',
        minHeight: 36,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 10px',
        borderRadius: '10px',
        justifyContent: 'flex-start',
        textAlign: 'left',
        color: danger
          ? theme.custom?.messageDangerIcon
          : theme.custom?.brandNavy,
        '& .row-icon': {
          color: danger
            ? theme.custom?.messageDangerIcon
            : theme.custom?.shellMenuIcon,
        },
        '&:hover': { background: theme.custom?.shellMenuItemHoverBg },
      }}
    >
      <Box className="row-icon" sx={{ display: 'flex' }}>
        {typeof icon === 'string' ? (
          <NavIcon name={icon as NavIconName} size={17} />
        ) : (
          icon
        )}
      </Box>
      <Box
        component="b"
        sx={{ flex: 1, minWidth: 0, fontSize: '14px', fontWeight: 500 }}
      >
        {label}
      </Box>
      {trailing}
    </ButtonBase>
  );
};

const MenuSection = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        padding: '4px 0',
        boxShadow: `inset 0 1px 0 ${theme.custom?.shellMenuDivider}`,
      }}
    >
      {children}
    </Box>
  );
};

export const AccountMenu = ({
  anchorEl,
  onClose,
  profileImage,
  setupHref,
  options,
}: {
  anchorEl: Element | null;
  onClose: () => void;
  profileImage?: string;
  setupHref?: string;
  options: AccountMenuOptions;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { username, onSignOut } = useAuth();
  const displayUsername = options.ssoUserName || username;
  const [profilesOpen, setProfilesOpen] = useState(false);
  const {
    accountData,
    profileOptions,
    selectedProfileId,
    currentProfile,
    isLoading: isSwitching,
    switchProfile,
  } = useProfileSwitch();
  // A support login works one facility at a time: it switches facility here
  // and doesn't get the user-only menu items.
  const isSupportSession = accountData?.isSupportSession ?? false;
  // The selected profile's department (from /me).
  const department = accountData?.profiles?.find(
    (profile) =>
      profile.id === (selectedProfileId || accountData?.tokenProfileId)
  )?.departmentLaborLevelDescription;

  const {
    isLoading: signOutLoading,
    showLoading: showSignOutLoading,
    hideLoading: hideSignOutLoading,
  } = useLoading();

  const { data: contactSupportPermission } = useContactSupportPermissionGet({
    enabled: Boolean(options.showContactUs),
  });

  const go = useCallback(
    (to: string) => {
      onClose();
      navigate(to);
    },
    [navigate, onClose]
  );

  const handleSignOut = useCallback(async () => {
    try {
      showSignOutLoading();
      await onSignOut();
    } catch (e) {
      /* empty */
    } finally {
      hideSignOutLoading();
    }
  }, [hideSignOutLoading, onSignOut, showSignOutLoading]);

  const confirmationModal = useConfirmationModal({ redesign: true });
  const { persistSessionToken } = useAccessToken();

  const openCleanConfigModal = async () => {
    onClose();
    await confirmationModal
      .openModal({
        title: 'Refresh configuration?',
        content:
          "This reloads SBV360 with the latest setup changes. You stay signed in, but anything you haven't saved on this page will be lost.",
        icon: 'none',
        confirmLabel: 'Refresh',
      })
      .then(() => {
        persistSessionToken();
        window.location.reload();
      })
      .catch(printErrorToConsole);
  };

  const canSwitchProfiles =
    options.showProfiles !== false && profileOptions.length > 1;

  return (
    <>
      {isSwitching && <OverlayLoader />}
      {confirmationModal.modal}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          backdrop: {
            invisible: false,
            sx: {
              backgroundColor: {
                xs: theme.custom?.shellScrim,
                md: 'transparent',
              },
            },
          },
          paper: {
            sx: {
              mt: '8px',
              width: 288,
              maxWidth: 'calc(100vw - 16px)',
              padding: '6px',
              borderRadius: '14px',
              boxShadow: theme.custom?.shellAccountMenuShadow,
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px',
          }}
        >
          <LetterAvatar
            name={displayUsername}
            src={profileImage}
            sx={{
              width: 40,
              height: 40,
              fontSize: '13.5px',
              fontWeight: 600,
              background: theme.custom?.shellAvatarBg,
              color: theme.background.white,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Box
              component="b"
              sx={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 700,
                color: theme.custom?.brandNavy,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayUsername}
            </Box>
            {accountData?.email && (
              <Box
                component="small"
                sx={{
                  display: 'block',
                  fontSize: '12.5px',
                  color: theme.custom?.shellMutedText,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {accountData.email}
              </Box>
            )}
            {isSupportSession && (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  mt: '4px',
                  padding: '2px 8px',
                  borderRadius: '999px',
                  fontSize: '11.5px',
                  fontWeight: 600,
                  color: theme.custom?.brandIndigo,
                  background: theme.custom?.shellRoleBg,
                }}
              >
                Support: {accountData?.username ?? username}
              </Box>
            )}
            {department && (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  mt: '4px',
                  padding: '2px 8px',
                  borderRadius: '999px',
                  fontSize: '11.5px',
                  fontWeight: 600,
                  color: theme.custom?.brandIndigo,
                  background: theme.custom?.shellRoleBg,
                }}
              >
                {department}
              </Box>
            )}
          </Box>
        </Box>

        {isSupportSession && (
          <Box sx={{ mb: '6px', '& .MuiInputBase-root': { width: '100%' } }}>
            <SupportFacilitySwitcher />
          </Box>
        )}
        {!isSupportSession &&
          currentProfile &&
          options.showProfiles !== false && (
            <>
              <ButtonBase
                onClick={() => canSwitchProfiles && setProfilesOpen((o) => !o)}
                disableRipple={!canSwitchProfiles}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 10px',
                  mb: '6px',
                  borderRadius: '10px',
                  textAlign: 'left',
                  cursor: canSwitchProfiles ? 'pointer' : 'default',
                  background: profilesOpen
                    ? theme.custom?.shellProfileOpenBg
                    : theme.custom?.shellProfileBg,
                }}
              >
                <NavIcon
                  name="building"
                  size={18}
                  sx={{ color: theme.custom?.brandIndigo }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    component="small"
                    sx={{
                      display: 'block',
                      fontSize: '11.5px',
                      lineHeight: '14px',
                      color: theme.custom?.shellMutedText,
                    }}
                  >
                    Profile
                  </Box>
                  <Box
                    component="b"
                    sx={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 700,
                      lineHeight: '18px',
                      color: theme.custom?.brandNavy,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {currentProfile.label}
                  </Box>
                </Box>
                {canSwitchProfiles && (
                  <NavIcon
                    name="chevron"
                    size={14}
                    sx={{
                      color: theme.custom?.shellNavChevron,
                      transform: profilesOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 150ms',
                    }}
                  />
                )}
              </ButtonBase>
              {profilesOpen && (
                <Box
                  sx={{
                    padding: '4px 0 4px 30px',
                    display: 'grid',
                    gap: '1px',
                    maxHeight: 260,
                    overflowY: 'auto',
                  }}
                >
                  {profileOptions.map((option) => {
                    const on = option.value === selectedProfileId;
                    return (
                      <ButtonBase
                        key={option.value}
                        disabled={isSwitching}
                        onClick={() => switchProfile(String(option.value))}
                        sx={{
                          width: '100%',
                          minHeight: 35,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                          padding: '8px 10px',
                          borderRadius: '8px',
                          textAlign: 'left',
                          fontSize: '13.5px',
                          fontWeight: on ? 600 : 400,
                          color: on
                            ? theme.custom?.brandNavy
                            : theme.custom?.shellMenuItemText,
                          background: on ? theme.custom?.shellRoleBg : 'none',
                          '&:hover': {
                            background: on
                              ? theme.custom?.shellRoleBg
                              : theme.custom?.shellMenuItemHoverBg,
                          },
                        }}
                      >
                        {option.label}
                        {on && (
                          <Box
                            component="i"
                            sx={{
                              fontStyle: 'normal',
                              color: theme.custom?.brandIndigo,
                            }}
                          >
                            ✓
                          </Box>
                        )}
                      </ButtonBase>
                    );
                  })}
                </Box>
              )}
            </>
          )}

        {setupHref && (
          <MenuSection>
            <MenuRow
              icon="setup"
              label="Setup / Configuration"
              onClick={() => go(setupHref)}
            />
          </MenuSection>
        )}

        <MenuSection>
          {options.showPreferences && !isSupportSession && (
            <MenuRow
              icon="preferences"
              label="Preferences"
              onClick={() => go('/preferences')}
            />
          )}
          {options.showResetConfiguration && !isSupportSession && (
            <MenuRow
              icon="refresh"
              label="Refresh configuration"
              onClick={openCleanConfigModal}
            />
          )}
          {options.showRemoteSupport && (
            <MenuRow
              icon="remoteSupport"
              label="Remote support"
              trailing={
                <Box
                  component="i"
                  sx={{
                    fontStyle: 'normal',
                    color: theme.custom?.shellNavChevron,
                  }}
                >
                  ↗
                </Box>
              }
              onClick={() => {
                onClose();
                window.open('https://sbv.screenconnect.com/');
              }}
            />
          )}
          {options.showContactUs &&
            !isSupportSession &&
            contactSupportPermission?.canContactSupport && (
              <MenuRow
                icon="contactUs"
                label="Contact us"
                onClick={() => go('/contact-us')}
              />
            )}
          {options.extraMenuItems?.map(({ label, icon, onClick }) => (
            <MenuRow
              key={label}
              icon={icon}
              label={label}
              onClick={() => {
                onClose();
                onClick();
              }}
            />
          ))}
        </MenuSection>

        <MenuSection>
          <MenuRow
            icon="signOut"
            label="Sign out"
            danger
            disabled={signOutLoading}
            onClick={handleSignOut}
          />
        </MenuSection>
      </Popover>
    </>
  );
};
